import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Lock, Crown, Gift, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const SHOP_CATEGORIES = [
  { value: 'lingerie', label: 'Lingerie' },
  { value: 'brinquedos', label: 'Brinquedos' },
  { value: 'exclusivos', label: 'Exclusivos' },
];
const FILTERS = [{ value: 'todos', label: 'Todos' }, ...SHOP_CATEGORIES];
const catLabel = (v) => SHOP_CATEGORIES.find((c) => c.value === v)?.label || v;

const CodeChip = ({ code, testId }) => (
  <button
    type="button"
    onClick={() => navigator.clipboard?.writeText(code).then(() => toast.success('Código copiado'))}
    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gold/60 px-2 py-0.5 font-mono text-xs text-gold hover:bg-gold/10 transition-colors"
    data-testid={testId}
  >
    {code} <Copy className="w-3 h-3" />
  </button>
);

const Benefits = ({ p, tier }) => {
  if (!p.free_benefit_label && !p.premium_benefit_label) return null;
  return (
    <div className="space-y-2 rounded-lg bg-white/5 p-3 text-sm" data-testid={`shop-benefits-${p.id}`}>
      {p.free_benefit_label && (
        <div className="flex flex-wrap items-center gap-2">
          <Gift className="w-4 h-4 text-ruby shrink-0" />
          <span className="text-muted-foreground">Membros:</span>
          <span className="text-foreground">{p.free_benefit_label}</span>
          {p.free_benefit_code && <CodeChip code={p.free_benefit_code} testId={`shop-free-code-${p.id}`} />}
        </div>
      )}
      {p.premium_benefit_label && (
        <div className="flex flex-wrap items-center gap-2">
          <Crown className="w-4 h-4 text-gold shrink-0" />
          <span className="text-muted-foreground">Premium:</span>
          <span className="text-gold font-medium">{p.premium_benefit_label}</span>
          {p.premium_benefit_code && <CodeChip code={p.premium_benefit_code} testId={`shop-premium-code-${p.id}`} />}
        </div>
      )}
      {tier !== 'premium' && p.has_premium_code && (
        <Link
          to="/premium"
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-gold/60 px-3 py-1.5 text-xs font-medium text-gold hover:bg-gold/10 transition-colors"
          data-testid={`shop-premium-cta-${p.id}`}
        >
          <Lock className="w-3.5 h-3.5" /> Torna-te Premium e desbloqueia o cupão
        </Link>
      )}
      {tier === 'anon' && p.has_free_code && (
        <p className="text-xs text-muted-foreground"><Link to="/login" className="text-ruby hover:underline">Entra</Link> para veres o teu código.</p>
      )}
    </div>
  );
};

const ProductCard = ({ p, tier }) => (
  <article className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-colors hover:border-ruby/50" data-testid={`shop-product-${p.id}`}>
    <div className="relative aspect-[4/5] overflow-hidden bg-white/5">
      {p.image_url
        ? <img src={p.image_url} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        : <div className="flex h-full items-center justify-center"><ShoppingBag className="w-10 h-10 text-muted-foreground" /></div>}
      <Badge className="absolute left-3 top-3 bg-black/70 text-foreground border-0">{catLabel(p.category)}</Badge>
    </div>
    <div className="flex flex-1 flex-col gap-3 p-5">
      <h3 className="text-lg font-semibold text-foreground" data-testid={`shop-product-name-${p.id}`}>{p.name}</h3>
      {p.short_description && <p className="text-sm text-muted-foreground line-clamp-3">{p.short_description}</p>}
      <Benefits p={p} tier={tier} />
      <Button asChild className="mt-auto bg-ruby hover:bg-ruby-light">
        <a href={p.premium_purchase_url || p.purchase_url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`shop-buy-${p.id}`}>
          Comprar <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </Button>
    </div>
  </article>
);

const ExclusivosLock = ({ tier, count }) => (
  <div className="rounded-xl border border-gold/40 bg-gradient-to-br from-gold/10 to-transparent p-8 md:p-10" data-testid="shop-exclusivos-locked">
    <div className="flex flex-col md:flex-row md:items-center gap-6">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-gold/15">
        <Lock className="w-6 h-6 text-gold" />
      </div>
      <div className="flex-1">
        <h2 className="text-lg font-semibold text-foreground">Exclusivos Premium</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {count > 0 ? `${count} produto${count > 1 ? 's' : ''} selecionado${count > 1 ? 's' : ''} só para membros Premium, ` : 'Uma seleção só para membros Premium, '}
          com vantagens reforçadas em toda a Loja.
        </p>
      </div>
      <Button asChild className="bg-gold text-black hover:bg-gold/90">
        <Link to={tier === 'anon' ? '/login' : '/premium'} data-testid="shop-become-premium">
          <Crown className="w-4 h-4 mr-2" /> {tier === 'anon' ? 'Entrar' : 'Tornar-me Premium'}
        </Link>
      </Button>
    </div>
  </div>
);

export default function Loja() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('todos');

  useEffect(() => {
    api.get('/shop/products').then((r) => setData(r.data)).catch(() => setData({ products: [], tier: 'anon' }));
  }, []);

  const visible = useMemo(
    () => (data?.products || []).filter((p) => filter === 'todos' || p.category === filter),
    [data, filter]
  );
  const showLock = data?.exclusive_locked && (filter === 'todos' || filter === 'exclusivos');

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <SEOHead title="Loja Intimate" description="Lingerie, brinquedos e exclusivos selecionados pela Intimate.pt, com vantagens para membros." type="website" />
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <header className="mb-10 max-w-2xl">
            <h1 className="font-brand text-4xl sm:text-5xl font-bold text-foreground" data-testid="shop-heading">Loja Intimate</h1>
            <p className="mt-3 text-muted-foreground">Uma seleção de parceiros, com vantagens para membros e benefícios reforçados para Premium.</p>
          </header>
          <div className="mb-8 flex flex-wrap gap-2" data-testid="shop-filters">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`rounded-full border px-5 py-2 text-sm transition-colors ${filter === f.value ? 'border-ruby bg-ruby text-white' : 'border-border text-muted-foreground hover:border-ruby/60 hover:text-foreground'}`}
                data-testid={`shop-filter-${f.value}`}
              >
                {f.value === 'exclusivos' && <Crown className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-gold" />}{f.label}
              </button>
            ))}
          </div>
          {!data ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-ruby" /></div>
          ) : (
            <div className="space-y-8">
              {visible.length > 0 && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
                  {visible.map((p) => <ProductCard key={p.id} p={p} tier={data.tier} />)}
                </div>
              )}
              {showLock && <ExclusivosLock tier={data.tier} count={data.exclusive_count} />}
              {visible.length === 0 && !showLock && (
                <p className="py-16 text-center text-muted-foreground" data-testid="shop-empty">Ainda não há produtos nesta categoria.</p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
